import type { ChatConversationDto } from "../../contracts";
import CommercialHistoryItemCard, { type CommercialHistoryItemLayoutType } from "../../pages/workspace/components/atoms/commercialHistoryItemCard";
import useCommercialHistoryItem, { type CommercialHistoryItemStatusType } from "../hooks/useCommercialHistoryItem";
import { CommercialHistoryItemLayoutTypes, CommercialHistoryItemStatusTypes } from "../../pages/workspace/components/types/commercialHistoryTypes";
import { toDate } from "../../utils/dateValueConversion";

type CommercialHistoryItemProps = {
    conversation: ChatConversationDto;
    isSelected: boolean;
    layout?: CommercialHistoryItemLayoutType;
    onSelect: (conversationId: number, status: CommercialHistoryItemStatusType) => void;
};

const formatWorkRecency = (value: Date | string | null | undefined): string | undefined => {
    const date = toDate(value);

    if (!date) {
        return undefined;
    }

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayDiff = Math.round((startOfToday.getTime() - startOfDate.getTime()) / 86400000);

    if (dayDiff <= 0) {
        return "Today";
    }

    if (dayDiff === 1) {
        return "Yesterday";
    }

    if (dayDiff < 7) {
        return `${dayDiff} days ago`;
    }

    if (dayDiff < 14) {
        return "1 week ago";
    }

    const weeks = Math.floor(dayDiff / 7);

    if (weeks < 5) {
        return `${weeks} weeks ago`;
    }

    return undefined;
};

const CommercialHistoryItem = ({
    conversation,
    isSelected,
    layout = CommercialHistoryItemLayoutTypes.DETAIL,
    onSelect
}: CommercialHistoryItemProps) => {
    const { title, contextLine, status, metaText } = useCommercialHistoryItem(conversation);
    const displayMeta = layout === CommercialHistoryItemLayoutTypes.INLINE && status !== CommercialHistoryItemStatusTypes.SIMULATION_RUNNING
        ? formatWorkRecency(conversation.last_message_at ?? conversation.created_at) ?? metaText
        : metaText;

    return (
        <CommercialHistoryItemCard
            badgeHasIcon
            title={title}
            contextLine={contextLine}
            status={status}
            metaText={displayMeta}
            isSelected={isSelected}
            layout={layout}
            onSelect={() => onSelect(conversation.id, status)}
        />
    );
};

export default CommercialHistoryItem;
